import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Before/after comparison.
 *
 * Pointer events rather than mouse or touch, for the same reason the signature
 * pad uses them: finger, stylus and trackpad through one code path, because
 * this gets looked at on a tablet as often as at a desk.
 *
 * Two real files now: `before` is the source picture straight off onOffice's
 * CDN, `after` is what the model returned, served by this API.
 */
export function CompareSlider({
  before,
  after,
  alt,
}: {
  before: string
  after: string
  alt: string
}) {
  const container = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState(50)
  const dragging = useRef(false)

  const moveTo = useCallback((clientX: number) => {
    const bounds = container.current?.getBoundingClientRect()
    if (!bounds || bounds.width === 0) return
    const ratio = ((clientX - bounds.left) / bounds.width) * 100
    setPosition(Math.min(100, Math.max(0, ratio)))
  }, [])

  useEffect(() => {
    const onMove = (event: PointerEvent) => {
      if (dragging.current) moveTo(event.clientX)
    }
    const onUp = () => {
      dragging.current = false
    }

    // On window, not the element: a drag that leaves the picture must keep
    // tracking rather than stopping at the edge.
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [moveTo])

  return (
    // The keyboard path is the range input at the bottom of this component,
    // deliberately a sibling rather than an overlay — see the comment there.
    // biome-ignore lint/a11y/noStaticElementInteractions: see above
    <div
      ref={container}
      className="bg-raised relative touch-none overflow-hidden select-none"
      onPointerDown={(event) => {
        // Also stops the text/image selection the browser would otherwise
        // start on the very first pixel of movement.
        event.preventDefault()
        dragging.current = true
        moveTo(event.clientX)
      }}
      onDragStart={(event) => event.preventDefault()}
    >
      {/* Capped: a portrait photo at full width would push the controls and the
          next job off the screen entirely. Both layers carry the SAME box and
          the same object-fit, or the clip would cut across a shifted image. */}
      <img
        src={before}
        alt={alt}
        referrerPolicy="no-referrer"
        draggable={false}
        className="no-drag block max-h-[60vh] w-full object-contain"
      />

      {/* The generated layer, clipped to the slider position. Gemini returns the
          input's aspect ratio, so the two stack without letterbox drift. */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 0 0 ${position}%)` }}
        aria-hidden
      >
        <img
          src={after}
          alt=""
          referrerPolicy="no-referrer"
          draggable={false}
          className="no-drag block max-h-[60vh] w-full object-contain"
        />
      </div>

      <span className="bg-canvas/85 text-muted absolute top-2 left-2 px-1.5 py-0.5 text-[9px] tracking-wider uppercase">
        Vorher
      </span>
      <span className="bg-canvas/85 text-muted absolute top-2 right-2 px-1.5 py-0.5 text-[9px] tracking-wider uppercase">
        Nachher
      </span>

      {/* Handle. A range input underneath keeps it operable by keyboard. */}
      <div
        className="pointer-events-none absolute inset-y-0 w-0.5 bg-white/90 shadow"
        style={{ left: `${position}%` }}
        aria-hidden
      >
        <span className="absolute top-1/2 left-1/2 flex size-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-[10px] text-black shadow">
          ◀▶
        </span>
      </div>

      {/*
        Keyboard access without stealing the pointer: an invisible full-width
        input laid over the picture swallows drags along its own strip, so the
        bottom of the image stops responding. Hidden until focused instead.
      */}
      <input
        type="range"
        min={0}
        max={100}
        value={Math.round(position)}
        onChange={(event) => setPosition(Number(event.target.value))}
        aria-label="Vergleich vorher/nachher"
        className="sr-only focus:not-sr-only focus:absolute focus:inset-x-4 focus:bottom-3 focus:z-10 focus:w-auto"
      />
    </div>
  )
}
